<template>
  <div class="other-amount-title">
    <h1>{{ t("superpacman-other-amount-title") }}</h1>
  </div>

  <div class="amount-to-be-paid-div">
    <div class="amount-to-be-paid-input-box">
      <label for="amount-to-be-paid-input">{{ currency.symbol }}</label>
      <input
        id="amount-to-be-paid-input"
        v-model="amountToBePaid"
        placeholder="0"
        type="text"
        inputmode="numeric"
        class="amount-to-be-paid-input"
        :style="amountInputCssVar"
        maxlength="14"
        autocomplete="off"
        @input="checkAmountToBePaid"
        @focus="stripDecimalOnFocus"
        @blur="formatAmountOnBlur"
      />
    </div>
    <p class="minimum-amount-title">{{ t("superpacman-minimum-amount") }}: {{ computedAmountValue(minimumAmount) }}</p>
    <div class="other-amount-conditional-messages">
      <p v-if="errorMessage" class="superpacman-other-amount-warning-text">
        {{ errorMessage }}
      </p>
      <p v-else-if="nextValidThrough" class="amount-just-right-text">
        <img class="superpacman-other-amount-info-icon" src="../assets/icons/info.svg" />
        {{ t("superpacman-next-payment-date") }}: {{ nextValidThrough }}
      </p>
    </div>
    <button id="confirm-other-amount-button" :disabled="!nextValidThrough" @click="handleClick">
      {{ t("superpacman-confirm-button") }}
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { ISuperPacmanInstallment } from "@/types/ISuperPacman";
import { IInstallmentDetail } from "@/types/IInstallment";
import { computedAmountValue } from "@/shared-helpers/language.helper";
import { Countries } from "@/types/Countries";

export default defineComponent({
  name: "OtherAmountView",
  setup() {
    const { n, t, d } = useI18n();
    const router = useRouter();
    const store = useStore();
    const superPacmanInstallment = computed(() => store.state.payOptions.superPacmanInstallment);
    const country = computed(() => superPacmanInstallment.value.financeProduct.country);
    const deviceTag = computed(() => store.state.payOptions.deviceInfo.deviceTag);
    const locale = computed(() => store.state.payOptions.user.locale);
    const currency = {
      symbol: store.state.payOptions.deviceInfo.currencySymbol,
      thousandSeparator: n(1000, "currency", locale.value).includes(".") ? "." : ",",
      decimalSeparator: n(1.1, "currency", locale.value).includes(".") ? "." : ",",
    };
    const paymentOption = computed(() => store.state.payOptions.deviceInfo.paymentOption);
    const nextValidThrough = ref("");
    const amountToBePaid = ref("");
    const errorMessage = ref("");
    const maximumAmount = computed(() => handleMaximumAmounts(superPacmanInstallment.value));
    const maximumAmountFormatted = computedAmountValue(Number(maximumAmount.value));
    const minimumAmount = computed(() => Number(superPacmanInstallment.value.financeProduct.minimumPaymentAmount));
    const amountInputCssVar = computed(() => ({
      "--input-width": `${amountToBePaid.value?.toString().length || 1}ch`,
    }));
    return {
      d,
      computedAmountValue,
      n,
      t,
      locale,
      store,
      router,
      amountInputCssVar,
      country,
      deviceTag,
      errorMessage,
      currency,
      paymentOption,
      amountToBePaid,
      minimumAmount,
      maximumAmount,
      maximumAmountFormatted,
      nextValidThrough,
      superPacmanInstallment,
    };
  },
  methods: {
    /**
     * Converts a currency formatted string into a number.
     * @param {string} amountString - The currency string to convert (e.g. "$1,234.56").
     * @returns {number} The numeric value (e.g. 1234.56).
     */
    currencyToNumber(amountString: string): number {
      const numericValueString = amountString.replace(new RegExp(`[^0-9${this.currency.decimalSeparator}]`, "g"), "");
      if (numericValueString.endsWith(this.currency.decimalSeparator)) {
        amountString = amountString.slice(0, -1);
      }
      const numericValue = Number(numericValueString.replace(this.currency.decimalSeparator, "."));
      return numericValue;
    },

    /**
     * Validates and formats the amount to be paid, updating error messages and next valid through date.
     */
    stripDecimalOnFocus() {
      if (!this.amountToBePaid.includes(this.currency.decimalSeparator)) return;
      const [intPart, decPart] = this.amountToBePaid.split(this.currency.decimalSeparator);
      // Strip ".00" on focus so the user edits from a clean integer.
      // Keep non-zero decimals (e.g. "98.40" stays "98.40", "98.00" becomes "98").
      if (/^0+$/.test(decPart)) {
        this.amountToBePaid = intPart;
      }
    },

    formatAmountOnBlur() {
      if (!this.amountToBePaid) return;
      const numericValue = this.currencyToNumber(this.amountToBePaid);
      if (!numericValue) return;
      const currencyValue = this.n(numericValue, "currency", this.locale);
      this.amountToBePaid = currencyValue.replace(this.currency.symbol, "").trim();
    },

    checkAmountToBePaid(event: Event) {
      // Strip any character that isn't a digit, decimal separator, or thousands separator.
      const validChars = new RegExp(`[^0-9${this.currency.decimalSeparator}${this.currency.thousandSeparator}]`, "g");
      this.amountToBePaid = this.amountToBePaid.replace(validChars, "");

      // Collapse multiple decimal separators — keep only the first one (e.g. "1..." → "1.").
      const decParts = this.amountToBePaid.split(this.currency.decimalSeparator);
      if (decParts.length > 2) {
        this.amountToBePaid = `${decParts[0]}${this.currency.decimalSeparator}${decParts.slice(1).join("")}`;
      }

      if (this.amountToBePaid.endsWith(this.currency.decimalSeparator)) return;

      const input = event.target as HTMLInputElement;
      const cursorPos = input.selectionStart ?? 0;
      const oldLen = this.amountToBePaid.length;
      const hasUserDecimal = this.amountToBePaid.includes(this.currency.decimalSeparator);

      // Truncate to 2 decimal places — prevents a third decimal digit from being
      // appended and rounding up the value (e.g. 100.489 → 100.49 → 100.50 counter effect).
      if (hasUserDecimal) {
        const [intPart, decPart] = this.amountToBePaid.split(this.currency.decimalSeparator);
        if (decPart && decPart.length > 2) {
          this.amountToBePaid = `${intPart}${this.currency.decimalSeparator}${decPart.substring(0, 2)}`;
        }
      }

      // Capture integer part length after truncation, before thousands-separator formatting,
      // so we can compute only the cursor shift caused by thousands separators (not truncation).
      const oldIntPartLen = this.amountToBePaid.split(this.currency.decimalSeparator)[0].length;

      const numericValue = this.currencyToNumber(this.amountToBePaid);
      if (isNaN(numericValue)) {
        this.errorMessage = "";
        this.nextValidThrough = "";
        return;
      }
      const currencyValue = this.n(numericValue, "currency", this.locale);
      const withoutSymbol = currencyValue.replace(this.currency.symbol, "").trim();

      // Don't append decimal places while typing the integer part — only add them
      // if the user has explicitly typed a decimal separator, or on blur.
      // Locales with minimumFractionDigits: 2 (e.g. es-PE) would otherwise jump
      // the cursor past ".00" on every keystroke.
      if (hasUserDecimal) {
        // Preserve the user's decimal digits as typed — don't let the formatter auto-append
        // trailing zeros (e.g. "60.6" → "60.60") which would push the cursor past the zero
        // and cause subsequent digits to be swallowed by truncation.
        const [, userDec] = this.amountToBePaid.split(this.currency.decimalSeparator);
        const intFormatted = withoutSymbol.split(this.currency.decimalSeparator)[0];
        this.amountToBePaid = `${intFormatted}${this.currency.decimalSeparator}${userDec}`;
      } else {
        this.amountToBePaid = withoutSymbol.split(this.currency.decimalSeparator)[0];
      }

      // Cursor shift from thousands-separator changes in the integer part only.
      // Truncation always removes from the end — it must not shift the cursor when
      // the cursor is already inside the decimal part.
      const newIntPartLen = this.amountToBePaid.split(this.currency.decimalSeparator)[0].length;
      const intPartDelta = newIntPartLen - oldIntPartLen;
      const decimalSepPos = hasUserDecimal ? this.amountToBePaid.indexOf(this.currency.decimalSeparator) : -1;
      const cursorInDecimalPart = hasUserDecimal && cursorPos > decimalSepPos;
      const newCursorPos = cursorInDecimalPart
        ? Math.min(cursorPos, this.amountToBePaid.length)
        : Math.max(0, cursorPos + intPartDelta);
      this.$nextTick(() => { input.setSelectionRange(newCursorPos, newCursorPos); });

      // Validate bounds of payable amounts.
      if (numericValue < this.minimumAmount) {
        this.errorMessage = this.t("superpacman-minimum-amount-warning");
      } else if (numericValue > this.maximumAmount) {
        // Messages depends on credit line payoff amount or cumulative balance.
        if (shouldDisplayCreditLineMaxAmountMessage(this.superPacmanInstallment)) {
          this.errorMessage = this.t("superpacman-max-amount-exceeded-creditline", {
            creditLinePayoffAmount: this.maximumAmountFormatted,
          });
        } else {
          this.errorMessage = this.t("superpacman-max-amount-exceeded-cumulative-balance", {
            fullPayoffAmount: this.maximumAmountFormatted,
          });
        }
      } else {
        this.errorMessage = "";
      }
      // Ensures that the inputted amount is between the minimumAmount and the maximumAmount.
      if (numericValue >= this.minimumAmount && numericValue <= this.maximumAmount) {
        this.nextValidThrough = this.calculateNextValidThrough(numericValue, this.superPacmanInstallment);
      } else {
        this.nextValidThrough = "";
      }
    },

    /**
     * Calculates the next valid through date for a given payment amount.
     * Iterates through superPacmanInstallment.daysToAmount and finds the latest dateUnlockedUntil
     * for which the amount is greater than or equal to the installment amount.
     * @param {number} amount - The payment amount.
     * @param {ISuperPacmanInstallment} superPacmanInstallment - The installment details.
     * @returns {string} The formatted next valid through date.
     */
    calculateNextValidThrough(amount: number, superPacmanInstallment: ISuperPacmanInstallment): string {
      let lowestValidthroughIndex = 0;
      for (let i = 0; i < superPacmanInstallment.daysToAmount.length; i++) {
        if (amount >= Number(superPacmanInstallment.daysToAmount[i].amount)) {
          lowestValidthroughIndex = i;
        }
      }
      // Multiply by 1000 because the Vue I18N library requires the UNIX timestamps to be in milliseconds.
      const date = superPacmanInstallment.daysToAmount[lowestValidthroughIndex].dateUnlockedUntil * 1000;
      return this.d(date, "medium", window.navigator.language);
    },

    /**
     * Handles the confirm button click for the other amount view.
     * Converts the input to a number, dispatches the selected installment, and navigates to the next view.
     */
    handleClick() {
      const amountToBePaidNumber = this.currencyToNumber(this.amountToBePaid);
      const installmentToPay: IInstallmentDetail = {
        days: 0,
        amount: amountToBePaidNumber,
        term: " ",
        isOverPayment: false,
      };
      this.store.dispatch("payOptions/setSelectedInstallment", installmentToPay);
      this.store.dispatch("payOptions/setSuperPacmanAmount", amountToBePaidNumber);
      this.store.dispatch("payOptions/setIsOtherAmount", true);
      this.router.push({ name: "ProvidersByDeviceTagAndCountry", params: { usingSP: "true" } });
    },
  },
});

/**
 * Determines the maximum payable amount for the other amount view.
 * As per VPOP-144, uses creditLinePayoffAmount unless cumulative balance is enabled, in which case uses fullPayoffAmount.
 * @param {ISuperPacmanInstallment} superPacmanInstallment - The installment details.
 * @returns {number} The maximum amount allowed.
 */
const handleMaximumAmounts = (superPacmanInstallment: ISuperPacmanInstallment) => {
  let maximumAmount = 0;
  const creditLinePayoffAmount = superPacmanInstallment.financeProduct.creditLinePayoffAmount
    ? Number(superPacmanInstallment.financeProduct.creditLinePayoffAmount)
    : null;
  const foMinPaymentWithCbPlusCLPayoffAmount = superPacmanInstallment.financeProduct
    .foMinPaymentWithCbPlusCLPayoffAmount
    ? Number(superPacmanInstallment.financeProduct.foMinPaymentWithCbPlusCLPayoffAmount)
    : null;
  const fullPayoffAmount = Number(superPacmanInstallment.financeProduct.fullPayoffAmount);
  /*
  This assumes that there can be a creditLinePayoffAmount without foMinPaymentWithCbPlusCLPayoffAmount.
  If that's the case, you must check first for the credit line payoff aount and use it.
  If it happens to be the case that you also have foMinPaymentWithCbPlusCLPayoffAmount, then use that instead.
  */
  if (creditLinePayoffAmount) {
    maximumAmount = creditLinePayoffAmount;
    if (foMinPaymentWithCbPlusCLPayoffAmount) {
      maximumAmount = foMinPaymentWithCbPlusCLPayoffAmount;
    }
  }

  if (superPacmanInstallment.cumulativeBalance.enabled === true) {
    // These checks might not be necessary since we already validate the amount strings in Finance Engine SDK.
    if (fullPayoffAmount) {
      maximumAmount = fullPayoffAmount;
    }
  }

  // Indonesia + Rental: cumulativeBalance is not enabled but we still allow Other Amount
  // and the upper bound should be the fullPayoffAmount.
  if (
    superPacmanInstallment.financeProduct.country === Countries.INDONESIA &&
    superPacmanInstallment.financeProduct.subProductVersion === "Rental" &&
    fullPayoffAmount
  ) {
    maximumAmount = fullPayoffAmount;
  }

  return maximumAmount;
};

/**
 * Determines if the credit line max amount message should be displayed.
 * As per VPOP-144, uses creditLinePayoffAmount unless cumulative balance is enabled.
 * @param {ISuperPacmanInstallment} superPacmanInstallment - The installment details.
 * @returns {boolean} True if the credit line max amount message should be shown.
 */
const shouldDisplayCreditLineMaxAmountMessage = (superPacmanInstallment: ISuperPacmanInstallment) => {
  if (superPacmanInstallment.cumulativeBalance.enabled === true) {
    return false;
  }

  const creditLinePayoffAmount = superPacmanInstallment.financeProduct.creditLinePayoffAmount
    ? Number(superPacmanInstallment.financeProduct.creditLinePayoffAmount)
    : null;
  return Boolean(creditLinePayoffAmount);
};
</script>

<style scoped>
input:focus {outline:none!important;}

.other-amount-title {
  margin: 16px;
}

.amount-to-be-paid-div {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 20px;
}

.amount-to-be-paid-input-box {
  margin-bottom: 10px;
  text-align: center;
}

.amount-to-be-paid-input-box label {
  text-align: center;
  font-family: "Inter";
  color: var(--Typography-Text-2);
  font-size: xxx-large;
}

.minimum-amount-title {
  font-size: smaller;
  color: var(--Typography-Text-2);
  margin-bottom: 0;
}

.amount-to-be-paid-input {
  width: var(--input-width, 1ch);
  max-width: 75%;
  height: auto;
  text-align: left;
  font-family: "Inter";
  color: var(--Typography-Text-2);
  font-size: xxx-large;
  background-color: var(--background-color);
  border: 0;
}

#confirm-other-amount-button:disabled {
  border: 1px solid #dddddd;
  background: #dddddd;
}

#confirm-other-amount-button {
  width: 92vw;
  height: 48px;
  flex-shrink: 0;
  border-radius: 10px;
  border: 1px solid var(--Primary-Green-1);
  background: var(--Primary-Green-1);
  margin-bottom: 40px;
  margin-left: 4vw;
  margin-right: auto;
  color: #ffffff;
  font-size: medium;
  font-weight: bold;
  margin-top: 58vh;
}

.other-amount-conditional-messages {
  width: auto;
  height: auto;
}

.amount-just-right-text {
  display: flex;
  width: 80vw;
  height: auto;
  border: 1px solid #d1d1d1;
  border-radius: 5px;
  max-width: 90vw;
  background: #f2f2f2;
  padding: 10px;
  font-size: 14px;
  font-weight: bold;
  margin: 0;
  margin-top: 15px;
  justify-content: center;
  color: var(--text-grey);
  padding-left: 15px;
  padding-right: 15px;
}

.superpacman-other-amount-info-icon {
  margin-right: 10px;
}

.superpacman-other-amount-warning-text {
  color: #c9372c;
  font-size: small;
  text-wrap: wrap;
  text-align: center;
  line-height: 17px;
}

@media screen and (max-height: 639px) {
  .button-grey-bg-white-text {
    margin-top: 19vh;
  }

  .button-green-bg-white-text {
    margin-top: 19vh;
  }
}

@media screen and (max-width: 320px) {
  .amount-to-be-paid-input {
    font-size: xx-large;
  }
}
</style>
